import { defineModule } from '../utils/helpers';
import { Overlay, toggleOverlay } from '../utils/overlays';

/**
 * Footer "Cookievoorkeuren" entry point for the Complianz cookie banner.
 *
 * Consent stays managed by Complianz — we only drive its native banner:
 *  - the footer trigger re-opens the banner and jumps straight to the
 *    preferences (categories) view, which our CSS renders as a modal;
 *  - clicking the scrim or pressing Escape dismisses it again.
 *
 * Complianz has no hook to open its banner straight into the preferences view, so
 * the trigger, the scroll lock and the focus position are driven from here.
 */

interface ComplianzConfig {
  consenttype?: string;
  user_banner_id?: string | number;
}

declare global {
  interface Window {
    complianz?: ComplianzConfig;
    cmplz_set_banner_status?: (status?: string) => void;
  }
}

const TRIGGER_SELECTOR = '[data-cookie-preferences]';
const TRIGGER_ITEM_SELECTOR = '[data-cookie-preferences-item]';
const CONTAINER_ID = 'cmplz-cookiebanner-container';
const CATEGORIES_VISIBLE = 'cmplz-categories-visible';

/** The consent types Complianz initialises a banner for; see show_cookie_banner(). */
const CONSENT_TYPES_WITH_BANNER = ['optin', 'optout'];

const FOCUSABLE = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** The banner Complianz picked for this visitor, by banner id and consent type. */
const getBanner = () => {
  const { consenttype: type, user_banner_id: id } = window.complianz ?? {};
  const selector =
    type && id
      ? `.cmplz-cookiebanner.banner-${id}.${type}`
      : '.cmplz-cookiebanner';

  return (
    document.querySelector<HTMLElement>(selector) ??
    document.querySelector<HTMLElement>('.cmplz-cookiebanner')
  );
};

/** The footer entries that re-open the banner, hidden until we know it works. */
const getTriggerItems = () =>
  document.querySelectorAll<HTMLElement>(TRIGGER_ITEM_SELECTOR);

let lastFocused: HTMLElement | null = null;
let observer: MutationObserver | null = null;

const isModalOpen = () => {
  const banner = getBanner();

  return Boolean(
    banner?.classList.contains('cmplz-show') &&
    banner?.classList.contains(CATEGORIES_VISIBLE),
  );
};

/**
 * Whether re-opening the banner does anything at all.
 *
 * Complianz skips `show_cookie_banner()` for any consent type other than
 * opt-in/opt-out — the site is configured EU-only, so every visitor it places
 * outside that region — and under Do Not Track. Its internal banner reference
 * then stays unset and `cmplz_set_banner_status('show')` is a no-op, so the
 * footer entry stays hidden instead of becoming a link that does nothing.
 */
const hasUsableBanner = () =>
  CONSENT_TYPES_WITH_BANNER.includes(window.complianz?.consenttype ?? '') &&
  typeof window.cmplz_set_banner_status === 'function' &&
  Boolean(getBanner());

const revealTrigger = () => {
  if (!hasUsableBanner()) return;

  getTriggerItems().forEach((item) => item.removeAttribute('hidden'));
};

/**
 * Complianz's template ships `role="dialog"`, `aria-modal` and `aria-labelledby`
 * already; re-assert them so the modal keeps its semantics if that markup changes,
 * and make sure the title it points at actually carries an id.
 */
const setDialogAttributes = (banner: HTMLElement) => {
  banner.setAttribute('role', 'dialog');
  banner.setAttribute('aria-modal', 'true');

  const title = banner.querySelector<HTMLElement>('.cmplz-title');
  if (!title) return;

  if (!title.id) title.id = 'cmplz-cookiebanner-title';
  banner.setAttribute('aria-labelledby', title.id);
};

const getFocusable = (banner: HTMLElement) =>
  [...banner.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => element.offsetParent !== null,
  );

/** Keep Tab inside the modal — it sits over an inert page. */
const trapFocus = (event: KeyboardEvent) => {
  const banner = getBanner();
  if (!banner) return;

  const focusable = getFocusable(banner);
  if (!focusable.length) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

/**
 * Mirror the banner's own state into the scroll lock and the focus position.
 *
 * Accept, deny and save-preferences are Complianz's buttons and close the modal
 * without passing through `dismiss()`, so the state is read off the banner
 * rather than tracked here.
 */
const syncModalState = () => {
  const open = isModalOpen();

  toggleOverlay(Overlay.MODAL, open);

  if (!open && lastFocused) {
    lastFocused.focus();
    lastFocused = null;
  }
};

/** Re-open the banner and expand straight to the preferences modal. */
const openPreferences = (trigger?: HTMLElement) => {
  const banner = getBanner();
  if (!banner) return;

  // Prefer the element that opened the modal over whatever holds focus: a click
  // does not focus a button in every browser, and focus should return to the
  // trigger either way.
  lastFocused =
    trigger ??
    (document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null);

  // Expand the categories first (while still hidden) to avoid a flash of the
  // compact banner before it becomes the modal.
  if (!banner.classList.contains(CATEGORIES_VISIBLE)) {
    banner.querySelector<HTMLElement>('.cmplz-view-preferences')?.click();
  }

  window.cmplz_set_banner_status?.('show');

  setDialogAttributes(banner);
  syncModalState();

  // Complianz only moves focus when it opens the banner itself, so re-opening from
  // the footer would leave focus on the trigger and the modal unreachable by
  // keyboard. `.cmplz-close` is hidden in the compact state, hence the fallback.
  (
    banner.querySelector<HTMLElement>('.cmplz-close') ?? getFocusable(banner)[0]
  )?.focus();
};

/** Dismiss the banner (Complianz records no consent — scripts stay blocked). */
const dismiss = () => {
  window.cmplz_set_banner_status?.('dismissed');
  syncModalState();
};

const handleClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  const trigger = target.closest<HTMLElement>(TRIGGER_SELECTOR);

  if (trigger) {
    event.preventDefault();
    openPreferences(trigger);
    return;
  }

  // A click on the scrim itself (not the modal card) closes the modal.
  if (target.id === CONTAINER_ID && isModalOpen()) {
    dismiss();
  }
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!isModalOpen()) return;

  if (event.key === 'Escape') {
    dismiss();
    return;
  }

  if (event.key === 'Tab') trapFocus(event);
};

/**
 * Watch the banner's class list: Complianz toggles `cmplz-show` and
 * `cmplz-categories-visible` there for every open and close, including the ones
 * its own buttons trigger.
 */
const observeBanner = () => {
  const banner = getBanner();
  if (!banner || observer) return;

  observer = new MutationObserver(syncModalState);
  observer.observe(banner, { attributeFilter: ['class'] });
};

const handleBannerLoaded = () => {
  revealTrigger();
  observeBanner();
};

export default defineModule(
  () => {
    document.addEventListener('click', handleClick);
    window.addEventListener('keydown', handleKeydown);
    // Fires at the end of show_cookie_banner(); never fires when Complianz
    // decides this visitor gets no banner.
    document.addEventListener(
      'cmplz_cookie_warning_loaded',
      handleBannerLoaded,
    );

    // The banner survives Swup navigations, so on a second page it is already
    // there and the event above has long fired.
    handleBannerLoaded();
  },
  () => {
    document.removeEventListener('click', handleClick);
    window.removeEventListener('keydown', handleKeydown);
    document.removeEventListener(
      'cmplz_cookie_warning_loaded',
      handleBannerLoaded,
    );

    observer?.disconnect();
    observer = null;

    toggleOverlay(Overlay.MODAL, false);
    lastFocused = null;
  },
);
